import { log as debugLog, error as debugError } from '@/utils/debugLogger';
import { synthesizeKanbanRoute } from './utils/synthesizeKanbanRoute';
import { probeTaskState } from './utils/probeTaskState';
import { waitFor } from './utils/waitFor';
import { ShareIntentError } from './errors';
import { taskExpandIntent } from './taskExpand';

/**
 * The "task-board" share intent: encode/decode a task into a deep-link URL
 * that lands the recipient on the Kanban view at the task's taskTypeId,
 * WITHOUT entering the 3D / 360 viewer.
 *
 * Use cases (driven by the copy-link menu in TaskCopyLinkButton):
 *   1. Sharing a task to a facility that has no harvested or 360-positioned
 *      tags for it — the 3D viewer would have nothing to show. Task-board
 *      mode shows the task on its Kanban card so the recipient at least
 *      sees its metadata, status, and comments.
 *   2. Auto-fallback target for `task-expand` URLs where the recipient
 *      facility lacks the linked tags (`task-expand.play` redirects here
 *      instead of throwing `task-has-no-tags`).
 *
 * URL: `${origin}/${facilityId}/?intent=task-board&taskId=${taskId}&taskTypeId=${taskTypeId}`
 *
 * Navigation strategy: the recipient's URL is the facility root, where
 * MainView mounts and dispatches `SHARE_INTENT/play`. This handler then
 * pushes the Kanban route via `rootState.mainApp.$router` — same pattern
 * the Vuex module already uses to reach `rootState.mainApp.$dialog`.
 *
 * `focusTaskId` is encoded into the pushed route's query string so the
 * future highlight+scroll enhancement (TODO at the bottom of `play`) can
 * read it from `$route.query.focusTaskId` without any further plumbing.
 */
export const taskBoardIntent = {
  type: 'task-board',

  /**
   * @param {{ facilityId: string, taskId: string, taskTypeId: string }} params
   * @returns {string}
   *
   * Note: taskTypeId is required because the Kanban route is parameterized
   * by it — we cannot derive it server-side from taskId alone (varies by
   * backend version, same constraint that drove taskExpand to encode it).
   */
  encode({ facilityId, taskId, taskTypeId }) {
    return `${window.location.origin}/${facilityId}/?intent=task-board&taskId=${taskId}&taskTypeId=${taskTypeId}`;
  },

  /**
   * @param {Record<string, string>} query
   * @returns {{ type: 'task-board', taskId: string, taskTypeId: string } | null}
   */
  decode(query) {
    if (query?.intent !== 'task-board' || !query?.taskId || !query?.taskTypeId) return null;
    return { type: 'task-board', taskId: query.taskId, taskTypeId: query.taskTypeId };
  },

  /**
   * @param {{ taskId: string, taskTypeId: string }} params
   * @param {{ dispatch: Function, commit: Function, state: object, rootState: object }} ctx
   */
  async play({ taskId, taskTypeId, _probed }, { dispatch, commit, rootState }) {
    debugLog(`[taskBoardIntent.play] starting taskId=${taskId} taskTypeId=${taskTypeId}`);

    // ─────────────────────────────────────────────────────────────────────
    // Self-healing URL: probe the task's CURRENT state and promote upward
    // to task-expand ONLY when the task is genuinely visualizable at the
    // current facility. "Has tags somewhere" is necessary but NOT sufficient
    // — a task with tags only at another facility can't open a meaningful
    // viewer here, so promoting would land the user in an empty fullscreen.
    //
    // Why this is safe (no infinite loop): the conditions used by the two
    // intents are exact inverses:
    //   - taskBoard.play promotes iff: hasTags AND canVisualize
    //   - taskExpand.play falls back iff: !hasTags OR !canVisualize
    // When taskBoard promotes, BOTH preconditions hold → taskExpand sees
    // neither of its fallback triggers → terminal at taskExpand.
    // When taskExpand falls back, AT LEAST ONE precondition fails → taskBoard
    // doesn't re-promote → terminal at taskBoard. One delegation hop max.
    //
    // The `_probed` param lets callers (taskExpand calling us back via
    // strict-fallback) reuse the probe result instead of refetching task +
    // tags. Total cost across the chain: one probe regardless of which
    // intent the URL specifies.
    //
    // Failure mode: probe.ok=false (network blip, deleted task) → all booleans
    // false → falls through to default task-board behavior (Kanban). Same
    // end-state as if there'd been no probe; degraded UX is "lands on Kanban,"
    // never "blank fullscreen viewer."
    const probe = _probed || (await probeTaskState(taskId, { dispatch, rootState }));

    if (probe.canVisualize) {
      debugLog('[taskBoardIntent.play] task is visualizable — promoting to task-expand', {
        taskId,
      });
      return taskExpandIntent.play(
        { taskId, taskTypeId, _probed: probe },
        { dispatch, commit, rootState },
      );
    }
    // ─────────────────────────────────────────────────────────────────────

    const router = rootState.mainApp?.$router;
    if (!router) {
      debugError('[taskBoardIntent.play] router not available on rootState.mainApp');
      throw new ShareIntentError('App not ready', 'forge-not-ready');
    }

    const facilityId = rootState.assetId;
    if (!facilityId) {
      debugError('[taskBoardIntent.play] no current facility id (rootState.assetId)');
      throw new ShareIntentError('Current facility unknown', 'forge-not-ready');
    }

    const route = synthesizeKanbanRoute({ taskTypeId }, facilityId);

    // Carry taskId forward as a query param so the future highlight+scroll
    // enhancement can pick it up from `$route.query.focusTaskId`. Costs
    // nothing today; saves a cross-cutting refactor when we add the polish.
    if (taskId) {
      route.query = { ...(route.query || {}), focusTaskId: taskId };
    }

    // ─────────────────────────────────────────────────────────────────────
    // Wait for the facility's 2D viewer init to complete BEFORE redirecting
    // to Kanban. Without this, the upcoming router.replace tears down the
    // facility component while launchPlanViewer is mid-flight (model still
    // loading via loadDocumentNode), leaving forgeService's shared state
    // half-initialized. When the user later returns to the viewer, the
    // corrupted state causes pageView's mount to fail with `insertBefore`
    // errors and the 2D pane is unusable until full reload.
    //
    // Signal source: launchPlanViewer (forgeviewer.js:150-234) sets
    // store.state.planUrn early (line 161), then resolves its Promise only
    // after planViewer.model is populated by loadDocumentNode().then()
    // (line 216). So `planViewer && planViewer.model` is the canonical
    // "2D fully loaded" check.
    //
    // Skipped when no 2D plan is loading (planUrn empty) — avoids a 10s
    // timeout penalty for facilities without a 2D plan.
    if (rootState.planUrn) {
      const planLoaded = await waitFor(
        () => !!rootState.planViewer && !!rootState.planViewer.model,
        { timeout: 10000 },
      );
      debugLog('[taskBoardIntent.play] 2D viewer wait completed', {
        planLoaded,
        timedOut: !planLoaded,
      });
    } else {
      debugLog('[taskBoardIntent.play] no planUrn — skipping 2D viewer wait');
    }
    // ─────────────────────────────────────────────────────────────────────

    debugLog('[taskBoardIntent.play] replacing url with kanban route', {
      facilityId,
      taskTypeId,
      focusTaskId: taskId,
    });

    // Use router.replace (NOT push) so the original `?intent=task-board` URL
    // is OVERWRITTEN in browser history rather than added on top of it. The
    // share-intent URL is a redirect: the user never meaningfully "visited"
    // it — they intended to visit its terminal destination (Kanban). If we
    // pushed instead, a subsequent history-back (browser back-button or any
    // app-level "Back to Viewer" / similar) would navigate to the intent
    // URL, MainView would re-decode it, this play() would run again, and we
    // would `router.push(kanban)` once more — producing the URL-flash bounce
    // the user reported. Replace eliminates that race entirely.
    try {
      await router.replace(route);
      debugLog('[taskBoardIntent.play] navigation completed');
    } catch (err) {
      // Vue Router throws on duplicate navigation (NavigationDuplicated) — that
      // means the user is ALREADY on this Kanban; treat as a successful no-op
      // rather than surfacing an error toast. Real navigation failures still
      // bubble up via the SHARE_INTENT/play error handler.
      if (err?.name === 'NavigationDuplicated') {
        debugLog('[taskBoardIntent.play] already on target route — no-op');
        return;
      }
      throw err;
    }

    // TODO(highlight-scroll): when this intent matures, the Kanban view should
    // read `$route.query.focusTaskId`, scroll the matching card into view,
    // and apply a brief highlight pulse to draw the eye. Out of scope for
    // the initial copy-link feature per the approved plan; the seam (the
    // `focusTaskId` query param) is in place so the future change is local
    // to Kanban.vue's mounted/route-watch path.
  },
};
